import type { SidebarDisplayMode } from '../../../../Common/SidebarPlugin/SidebarContext';

/**
 * Semantic description of the AI Workspace's current layout mode.
 * Derived from the generic SidebarPlugin's `displayMode` + `isExpanded`
 * so the AI module never re-implements dock/overlay/close logic itself.
 *
 * | displayMode | isExpanded | AiWorkspaceMode    |
 * |-------------|------------|--------------------|
 * | 'docked'    | false      | 'sidebar'          |
 * | 'docked'    | true       | 'expanded-sidebar' |
 * | 'overlay'   | false      | 'overlay'          |
 * | 'overlay'   | true       | 'fullscreen'       |
 */
export type AiWorkspaceMode =
  | 'sidebar'
  | 'expanded-sidebar'
  | 'overlay'
  | 'fullscreen';

/**
 * Pure function — safe to call in render, memoize, or unit-test independently.
 * `SidebarContainer` imports this to eliminate its own `isDocked`/`isFullPage`
 * boolean algebra; AI content components import it from `useAskAISidebar`
 * (which calls this once and passes `mode` down via `sharedState`).
 */
export const resolveWorkspaceMode = (
  displayMode: SidebarDisplayMode,
  isExpanded: boolean,
): AiWorkspaceMode => {
  if (displayMode === 'docked') return isExpanded ? 'expanded-sidebar' : 'sidebar';
  return isExpanded ? 'fullscreen' : 'overlay';
};
